import { useEffect, useState } from "react";
import api from "../lib/api";
import { Map, Loader2 } from "lucide-react";

const TIER_TONE = {
  low: "bg-[#114232]/12 text-[#114232]",
  medium: "bg-[#d49a36]/20 text-[#8a5a16]",
  high: "bg-[#c15337]/15 text-[#8b3a23]",
};

// Hex fills used by the inline SVG choropleth.
const TIER_FILL = { low: "#114232", medium: "#d49a36", high: "#c15337" };

// Hand-curated centroids (very rough) for the African countries Fortiqa
// currently operates in. Used to position circular markers on a simple
// outline map. SVG viewBox is 0 0 400 400; coords are pre-projected.
// Source: visually centred against a Natural Earth outline.
const COUNTRY_DOTS = {
  KE: { x: 268, y: 196, label: "Kenya" },
  TZ: { x: 250, y: 232, label: "Tanzania" },
  UG: { x: 240, y: 184, label: "Uganda" },
  RW: { x: 232, y: 200, label: "Rwanda" },
  ZA: { x: 196, y: 332, label: "South Africa" },
  NG: { x: 122, y: 192, label: "Nigeria" },
  GH: {  x: 88, y: 196, label: "Ghana" },
  EG: { x: 200, y: 108, label: "Egypt" },
};

// Very simplified Africa silhouette path (single Polygon). Good enough as a
// non-interactive backdrop — drawn from a small fixed array of coords.
const AFRICA_PATH = (
  "M155,28 L210,18 L260,40 L300,72 L310,118 L308,154 L322,196 " +
  "L334,236 L324,278 L298,320 L260,350 L218,372 L172,366 L138,332 " +
  "L108,290 L88,242 L78,194 L88,148 L108,98 L126,60 Z"
);

export default function RiskMapCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/risk-map").then((r) => setData(r.data)).catch(() => setData({ regions: [], totals: {} }));
  }, []);

  const regionByCountry = (data?.regions || []).reduce((acc, r) => {
    acc[r.country] = r; return acc;
  }, {});

  return (
    <div className="bg-white border border-border rounded-md p-5" data-testid="risk-map-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Map className="w-4 h-4 text-[#114232]" />
          <h3 className="font-heading text-base font-bold text-[#114232]">Risk Map by Region</h3>
        </div>
        {data?.totals?.regions != null && (
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold font-mono">
            {data.totals.regions} regions · KES {data.totals.exposure_kes?.toLocaleString()} exposure
          </span>
        )}
      </div>
      {!data ? (
        <div className="py-6 text-center text-xs text-[#5c6360]" data-testid="risk-map-loading">
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading regions…
        </div>
      ) : data.regions.length === 0 ? (
        <div className="py-6 text-center text-xs text-[#5c6360]">No regions to plot yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6 items-start">
          {/* Choropleth */}
          <div className="bg-[#114232]/3 rounded-md p-3" data-testid="risk-map-svg-wrap">
            <svg viewBox="0 0 400 400" width="100%" height="240" data-testid="risk-map-svg" role="img" aria-label="Africa risk map">
              <path d={AFRICA_PATH} fill="#114232" fillOpacity="0.05" stroke="#114232" strokeOpacity="0.25" strokeWidth="1.4" />
              {Object.entries(COUNTRY_DOTS).map(([cc, c]) => {
                const r = regionByCountry[cc];
                if (!r) return null;
                const radius = Math.max(6, Math.min(22, Math.round(Math.log2(1 + r.merchant_count) * 5)));
                const fill = TIER_FILL[r.risk_tier] || "#5c6360";
                return (
                  <g key={cc} data-testid={`risk-map-marker-${cc}`}>
                    <circle cx={c.x} cy={c.y} r={radius} fill={fill} fillOpacity="0.6" stroke={fill} strokeWidth="1.6">
                      <title>{`${c.label} · ${r.merchant_count} merchants · avg score ${r.avg_score} · ${r.risk_tier}`}</title>
                    </circle>
                    <text x={c.x} y={c.y + 3} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="700">
                      {r.merchant_count}
                    </text>
                  </g>
                );
              })}
            </svg>
            <div className="flex items-center justify-center gap-3 mt-2 text-[10px] uppercase tracking-wider">
              {Object.entries(TIER_FILL).map(([t, f]) => (
                <span key={t} className="inline-flex items-center gap-1 text-[#5c6360] font-semibold">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: f }} />{t}
                </span>
              ))}
            </div>
          </div>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="risk-map-table">
              <thead className="text-[10px] uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="text-left font-medium py-2">Country</th>
                  <th className="text-left font-medium py-2">Merchants</th>
                  <th className="text-left font-medium py-2">Avg score</th>
                  <th className="text-left font-medium py-2">Exposure</th>
                  <th className="text-left font-medium py-2">Claims</th>
                  <th className="text-right font-medium py-2">Risk</th>
                </tr>
              </thead>
              <tbody>
                {data.regions.map((r) => (
                  <tr key={r.country} className="border-t border-border" data-testid={`risk-map-row-${r.country}`}>
                    <td className="py-2.5 font-mono">{r.country}</td>
                    <td className="py-2.5 font-mono">{r.merchant_count}</td>
                    <td className="py-2.5 font-mono">{r.avg_score}</td>
                    <td className="py-2.5 font-mono">KES {r.exposure_kes.toLocaleString()}</td>
                    <td className="py-2.5 font-mono">{r.claim_count}</td>
                    <td className="py-2.5 text-right">
                      <span className={`px-2 py-0.5 rounded font-semibold text-[10px] uppercase tracking-wider ${TIER_TONE[r.risk_tier] || ""}`}>
                        {r.risk_tier}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
